// Validation utilities for React app - ported from customer portal

export const EPARCEL = 'eParcel';

// Helper function to get courier ID by name
export const getCourierIdByName = (courierName: string): number => {
    const courierMap: { [key: string]: number } = {
        'Couriers Please': 1,
        'Star Track': 2,
        'TNT': 3,
        'AlliedExpress': 4,
        'Toll': 5,
        'Aramex': 6,
        'Hunter Express': 7,
        'Toll Palletised Express': 8,
        'Direct Couriers': 9,
        'Alpha Freight': 10,
        'MRL Global': 11,
        'Zoom2u': 12,
        'Northline': 13,
        'Northline Express': 20,
        'Felix Transport': 16,
        'Capital Transport': 17,
        'Bonds Couriers': 11,
        'TGE': 24
    };
    return courierMap[courierName] || 0;
};

// Helper function to get character limits for courier
export const getCharacterLimits = (courierId: number) => {
    const limits: { [key: string]: { [key: string]: number } } = {
        1: { // Couriers Please
            first_name: 15, last_name: 15, company_name: 19, address: 19, phone_number: 14
        },
        2: { // Star Track
            first_name: 40, last_name: 40, company_name: 19, address: 40, phone_number: 14
        },
        3: { // TNT
            first_name: 15, last_name: 14, company_name: 30, address: 30, phone_number: 13
        },
        4: { // Allied Express
            first_name: 40, last_name: 40, address: 100, phone_number: 14
        },
        5: { // Toll
            first_name: 19, last_name: 19, company_name: 40, address: 40, phone_number: 14
        },
        6: { // Aramex
            first_name: 40, last_name: 40, address: 30, phone_number: 14
        },
        7: { // Hunter Express
            first_name: 40, last_name: 40, address: 40, phone_number: 14
        },
        8: { // Toll Palletised Express
            first_name: 20, last_name: 20, company_name: 40, address: 30, phone_number: 10
        },
        9: { // Direct Couriers
            first_name: 40, last_name: 40, address: 40, phone_number: 14
        },
        11: { // Bonds Couriers
            first_name: 10, last_name: 10, company_name: 50, address: 30, phone_number: 14
        },
        12: { // Zoom2u
            first_name: 40, last_name: 40, address: 40, phone_number: 14
        },
        13: { // Northline
            first_name: 14, last_name: 14, company_name: 50, address: 30, phone_number: 10
        },
        16: { // Felix Transport
            first_name: 40, last_name: 40, address: 40, phone_number: 14
        },
        17: { // Capital Transport
            first_name: 15, last_name: 15, company_name: 20, address: 30, phone_number: 14
        },
        20: { // Northline New
            first_name: 14, last_name: 14, company_name: 50, address: 30, phone_number: 10
        }
    };
    return limits[courierId] || { first_name: 40, last_name: 40, company_name: 40, address: 40, phone_number: 14 };
};

// Helper function to get parcel content limit
export const getParcelContentLimit = (courierId: number): number => {
    const limits: { [key: number]: number } = {
        11: 30, // Bonds Couriers
        13: 80, // Northline
        20: 80  // Northline New
    };
    return limits[courierId] || 80;
};

// Validation result interface
export interface ValidationResult {
    isValid: boolean;
    errorMessage: string;
}

// Helper function to validate individual step 3 fields
export const validateStep3Field = (
    fieldName: string,
    fieldValue: string,
    courierId: number,
    courierName: string,
    charLimits: { [key: string]: number },
    buildingType?: string
): ValidationResult => {
    let isValid = true;
    let errorMessage = '';

    const nameRegex = /^[A-Za-z\s']+$/;
    const emailRegex = /^[a-zA-Z0-9](?:[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/;
    const phoneRegex = /^[+0-9]/;
    const valueRegex = /^\d+(\.\d{0,2})?$/;

    /* ------------------- NAME ------------------- */
    if (fieldName.includes('FirstName') || fieldName.includes('LastName')) {
        const maxLength = fieldName.includes('FirstName')
            ? (charLimits.first_name || 40)
            : (charLimits.last_name || 40);

        if (!fieldValue) {
            errorMessage = fieldName.includes('FirstName') ? 'First Name is required' : 'Last Name is required';
        } else if (!nameRegex.test(fieldValue)) {
            errorMessage = 'Only letters, spaces, and apostrophes are allowed';
        } else if (fieldValue.length > maxLength) {
            errorMessage = `Maximum ${maxLength} characters allowed`;
        }
    }

    /* ------------------- COMPANY ------------------- */
    else if (fieldName.includes('CompanyName')) {
        const maxLength = charLimits.company_name || 40;

        if (buildingType === 'commercial' && !fieldValue) {
            errorMessage = 'Company name is required for commercial addresses';
        } else if (fieldValue && fieldValue.length > maxLength) {
            errorMessage = `Maximum ${maxLength} characters allowed`;
        }
    }

    /* ------------------- ADDRESS ------------------- */
    else if (fieldName.includes('Address1') || fieldName.includes('Address2')) {
        const isRequired = fieldName.includes('Address1');
        const maxLength = charLimits.address || 40;

        if (isRequired && !fieldValue) {
            errorMessage = fieldName.includes('Address1') ? 'Address is required' : 'Address 2 is required';
        } else if (fieldValue && fieldValue.length > maxLength) {
            errorMessage = `Maximum ${maxLength} characters allowed`;
        }
    }

    /* ------------------- STREET ------------------- */
    else if (fieldName.includes('StreetName') || fieldName.includes('StreetNumber')) {
        if ([12, 16].includes(courierId) && !fieldValue) {
            errorMessage = fieldName.includes('StreetNumber') ? 'Street Number is required for this courier' : 'Street Name is required for this courier';
        }
    }

    /* ------------------- PHONE ------------------- */
    else if (fieldName.includes('Phone')) {
        const maxLength = charLimits.phone_number || 14;

        if (!fieldValue) {
            errorMessage = 'Phone number is required';
        } else if (!phoneRegex.test(fieldValue)) {
            errorMessage = 'Phone number must start with + or 0-9';
        } else if (fieldValue.replace(/[^0-9]/g, '').length < 8) {
            errorMessage = 'Phone number must be at least 8 digits';
        } else if (fieldValue.length > maxLength) {
            errorMessage = `Maximum ${maxLength} characters allowed`;
        }
    }

    /* ------------------- EMAIL ------------------- */
    else if (fieldName.includes('Email')) {
        if (!fieldValue) {
            errorMessage = 'Email address is required';
        } else if (!emailRegex.test(fieldValue) || /\.{2,}/.test(fieldValue)) {
            errorMessage = 'Please enter a valid email address';
        }
    }

    /* ------------------- COLLECTION DATE ------------------- */
    else if (fieldName === 'collectionDate') {
        if (!fieldValue) {
            errorMessage = 'Collection date is required';
        } else {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            let selectedDate: Date | null = null;

            // Validate date format first (dd-mm-yyyy)
            const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
            if (!dateRegex.test(fieldValue)) {
                errorMessage = 'Invalid date format. Please use DD-MM-YYYY format';
            } else {
                const [day, month, year] = fieldValue.split('-');
                selectedDate = new Date(`${year}-${month}-${day}`);
                // Check if the date is valid
                if (isNaN(selectedDate.getTime())) {
                    errorMessage = 'Invalid date. Please check the date values';
                } else if (selectedDate < today) {
                    errorMessage = 'Collection date cannot be in past';
                }
            }

            if ([5, 8, 20, 24].includes(courierId) && selectedDate && !isNaN(selectedDate.getTime())) {
                const daysDiff = Math.floor((selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                if (daysDiff > 7) {
                    errorMessage = 'Collection date should not be greater than 7 days';
                }
            }
        }
    }

    /* ------------------- PICKUP WINDOW ------------------- */
    else if (fieldName === 'pickupTimeWindow' && !fieldValue) {
        errorMessage = 'Pickup time window is required';
    }

    /* ------------------- PARCEL CONTENT ------------------- */
    else if (fieldName === 'parcelContent') {
        const maxLength = getParcelContentLimit(courierId);

        if (!fieldValue) {
            errorMessage = 'Shipment Content is required';
        } else if (fieldValue.length > maxLength) {
            errorMessage = `Maximum ${maxLength} characters allowed`;
        }
    }

    /* ------------------- VALUE ------------------- */
    else if (fieldName === 'valueOfContent') {
        if (!fieldValue) {
            errorMessage = 'Value of Content is required';
        } else if (!valueRegex.test(fieldValue)) {
            errorMessage = 'Please enter a valid monetary value';
        } else if (fieldValue.length > 8) {
            errorMessage = 'Maximum 8 digits allowed';
        }
    }

    /* ------------------- EXTENDED LIABILITY ------------------- */
    else if (fieldName === 'extendedLiability') {
        // Skip validation for My Post and Eparcel couriers
        if (courierName !== 'My Post' && courierName !== EPARCEL) {
            // Note: This would need access to items array from store
            // For now, we'll implement basic validation
            if (!fieldValue) {
                errorMessage = 'Please select an extended liability option';
            }
        }
    }

    /* ------------------- SPECIAL INSTRUCTIONS ------------------- */
    else if (fieldName === 'specialInstructions' && fieldValue.length > 30) {
        errorMessage = 'Maximum 30 characters allowed';
    }

    if (errorMessage) {
        isValid = false;
    }

    return { isValid, errorMessage };
};

// Function to convert field names to readable format
export const formatFieldName = (fieldName: string): string => {
    return fieldName.replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase())
              .replace(/pickup|destination/g, match => match === 'pickup' ? 'Pickup' : 'Destination')
              .trim();
};

// Comprehensive validation function for all step 3 fields
export const validateAllStep3Fields = (
    formData: { [key: string]: string },
    courierName: string,
    items?: any[]
): { isValid: boolean; errorFields: string[]; firstErrorField: string | null } => {
    const courierId = getCourierIdByName(courierName);
    const charLimits = getCharacterLimits(courierId);
    
    let isValid = true;
    let errorFields: string[] = [];
    let firstErrorField: string | null = null;

    // Validate each field
    Object.keys(formData).forEach(fieldName => {
        // Coerce to string first: some fields (e.g. valueOfContent, extendedLiability)
        // can arrive as numbers/booleans when the store is hydrated from the API
        // (e.g. the Official Quote deep link), and calling .trim() on those throws.
        const rawValue = formData[fieldName];
        const fieldValue = (rawValue === null || rawValue === undefined ? '' : String(rawValue)).trim();
        const buildingType = fieldName.includes('pickup')
            ? formData.pickupBuildingType
            : formData.destinationBuildingType;

        const result = validateStep3Field(
            fieldName,
            fieldValue,
            courierId,
            courierName,
            charLimits,
            buildingType
        );

        if (!result.isValid) {
            isValid = false;
            errorFields.push(fieldName);

            if (!firstErrorField) {
                firstErrorField = fieldName;
            }
        }
    });

    return { isValid, errorFields, firstErrorField };
};
